/**
 * "Finding your match…" overlay — the real, timer-less matchmaking state.
 * Shown on a fresh connect while the room is in `warmup` (the player is
 * alone), hidden the moment a second player joins and the match starts
 * (phase leaves warmup). Pointer-events are disabled so the player can
 * still warm up (move / shoot) in the arena underneath while waiting.
 * The only interactive element is the CANCEL button (pointer-events
 * re-enabled on it), which leaves matchmaking and returns to the lobby
 * by posting `hupper-fps-exit` to the embedding web app — the same
 * signal MatchEndOverlay / ConnectionOverlay use for "Back to lobby".
 *
 * The card visual is the "FPS Searching Card" design (Hupper handoff):
 * a compact panel with a gradient ring spinner, the stake sub-line, and
 * a live elapsed counter. Public API (`show` / `hide`) is unchanged.
 */
export class SearchingOverlay {
  private root: HTMLDivElement;
  private timer: number | null = null;
  private elapsed = 0;

  constructor() {
    const el = document.createElement("div");
    el.id = "searching-overlay";
    el.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:9997",
      "display:none",
      "align-items:center",
      "justify-content:center",
      "pointer-events:none",
      "font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif",
      "color:#fff",
    ].join(";");
    document.body.appendChild(el);
    this.root = el;
  }

  show(stakeLabel?: string): void {
    const sub =
      stakeLabel && stakeLabel !== "0"
        ? `<div class="so-sub">
             <span><span class="so-pot">$${stakeLabel}</span> stake</span>
             <span class="so-sep">·</span>
             <span>Winner takes all</span>
           </div>`
        : "";

    this.root.innerHTML = `
      <style>
        #searching-overlay .so-card{
          width:320px;border-radius:18px;padding:20px 22px 18px;position:relative;overflow:hidden;
          background:
            radial-gradient(ellipse 90% 50% at 50% -10%, rgba(255,87,34,0.10), transparent 60%),
            linear-gradient(180deg, rgba(24,20,17,0.96), rgba(12,10,8,0.98));
          box-shadow:0 40px 100px -30px rgba(0,0,0,0.7),0 0 0 1px #322a22,inset 0 1px 0 rgba(255,138,74,0.07);
          backdrop-filter:blur(12px);color:#f5efe6;
          font-family:'Geist',-apple-system,BlinkMacSystemFont,sans-serif;-webkit-font-smoothing:antialiased;
        }
        #searching-overlay .so-top{display:flex;align-items:center;gap:16px;}
        #searching-overlay .so-spinner{position:relative;width:48px;height:48px;flex-shrink:0;}
        #searching-overlay .so-spinner svg{display:block;transform:rotate(-90deg);}
        #searching-overlay .so-ring-track{stroke:rgba(255,255,255,0.07);}
        #searching-overlay .so-ring-prog{
          stroke:url(#so-grad);stroke-linecap:round;stroke-dasharray:132;stroke-dashoffset:95;
          animation:so-spin 1s linear infinite;transform-origin:center;
          filter:drop-shadow(0 0 5px rgba(255,87,34,0.6));
        }
        @keyframes so-spin{to{transform:rotate(360deg);}}
        #searching-overlay .so-txt{text-align:left;min-width:0;}
        #searching-overlay .so-txt h1{margin:0 0 5px;font-size:17px;font-weight:700;letter-spacing:-0.4px;color:#f5efe6;white-space:nowrap;}
        #searching-overlay .so-ell{color:#ff5722;}
        #searching-overlay .so-sub{
          display:inline-flex;align-items:center;gap:8px;
          font-family:'Geist Mono',ui-monospace,monospace;font-size:11.5px;color:#8a7f72;letter-spacing:0.2px;white-space:nowrap;
        }
        #searching-overlay .so-pot{color:#ff5722;font-weight:600;}
        #searching-overlay .so-sep{color:#5a5247;}
        #searching-overlay .so-foot{
          margin-top:16px;padding-top:14px;border-top:1px solid #25201a;
          display:flex;align-items:center;justify-content:space-between;
        }
        #searching-overlay .so-elapsed{
          display:inline-flex;align-items:center;gap:8px;
          font-family:'Geist Mono',ui-monospace,monospace;font-size:11px;color:#5a5247;letter-spacing:1px;
        }
        #searching-overlay .so-cancel{
          appearance:none;border:1px solid #322a22;background:transparent;color:#8a7f72;
          font-family:'Geist Mono',ui-monospace,monospace;font-size:11px;letter-spacing:1.2px;font-weight:600;
          padding:7px 14px;border-radius:8px;cursor:pointer;transition:all 160ms ease;
          pointer-events:auto;
        }
        #searching-overlay .so-cancel:hover{color:#f5efe6;border-color:#8a7f72;background:rgba(255,255,255,0.03);}
      </style>
      <div class="so-card">
        <div class="so-top">
          <div class="so-spinner">
            <svg width="48" height="48" viewBox="0 0 48 48">
              <defs>
                <linearGradient id="so-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stop-color="#ff8a4a"/>
                  <stop offset="100%" stop-color="#ff5722"/>
                </linearGradient>
              </defs>
              <circle class="so-ring-track" cx="24" cy="24" r="21" fill="none" stroke-width="3.5"/>
              <circle class="so-ring-prog" cx="24" cy="24" r="21" fill="none" stroke-width="3.5"/>
            </svg>
          </div>
          <div class="so-txt">
            <h1>Finding your match<span class="so-ell">…</span></h1>
            ${sub}
          </div>
        </div>
        <div class="so-foot">
          <span class="so-elapsed"><span class="so-elapsed-val">0:00</span> ELAPSED</span>
          <button class="so-cancel" type="button">CANCEL</button>
        </div>
      </div>
    `;
    this.root.style.display = "flex";
    this.startTimer();

    // CANCEL leaves matchmaking and returns to the lobby. The embedding web
    // app tears down the FPS iframe when it receives `hupper-fps-exit`
    // (same exit signal as MatchEndOverlay / ConnectionOverlay).
    this.root
      .querySelector<HTMLButtonElement>(".so-cancel")
      ?.addEventListener("click", () => {
        window.parent?.postMessage({ type: "hupper-fps-exit" }, "*");
      });
  }

  hide(): void {
    this.stopTimer();
    this.root.style.display = "none";
    this.root.innerHTML = "";
  }

  /**
   * Flip the visible card between its two states: searching ("Finding your
   * match…") and matched ("Opponent found — loading…"). The matched state
   * covers the pre-match load gate — the server holds the room in warmup
   * until both clients report their assets loaded — and hides CANCEL (the
   * stakes are already locking; backing out is no longer a lobby-side
   * cancel). Flipping back covers an opponent bailing mid-load. No-op while
   * the overlay is hidden (reconnect relaunch / match already started).
   */
  setMatchFound(found: boolean): void {
    if (this.root.style.display === "none") return;
    const h1 = this.root.querySelector<HTMLHeadingElement>(".so-txt h1");
    if (h1) {
      h1.innerHTML = found
        ? `Opponent found — loading<span class="so-ell">…</span>`
        : `Finding your match<span class="so-ell">…</span>`;
    }
    const cancel = this.root.querySelector<HTMLButtonElement>(".so-cancel");
    if (cancel) cancel.style.visibility = found ? "hidden" : "visible";
  }

  private startTimer(): void {
    this.stopTimer();
    this.elapsed = 0;
    const valEl = this.root.querySelector<HTMLSpanElement>(".so-elapsed-val");
    this.timer = window.setInterval(() => {
      this.elapsed += 1;
      if (valEl) {
        const m = Math.floor(this.elapsed / 60);
        const s = String(this.elapsed % 60).padStart(2, "0");
        valEl.textContent = `${m}:${s}`;
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }
}
